import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ExpandMore,
  Download as DownloadIcon,
  Upload as UploadIcon,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import UploadContractModal from '../../components/Contracts/UploadContractModal';
import { toFormData } from '../../components/Contracts/uploadContractForm';
import { buildUploadUrl, downloadDocument, downloadFileFromPath, fetchDocumentBlobUrl, fetchFileBlobUrl, isFallbackDocument } from '../../utils/fileAccess';
import StatusLabel from '../../components/Status/StatusLabel';
import ContractActivity from '../../components/Contracts/ContractActivity';
import { canSeeActivity } from '../../components/Contracts/activityTimeline';
import { canOpenContract, contractStatusLabel, displayContractStatus } from '../../utils/contractStatus';
import { canSetApplicability, APPLICABILITY_LABEL } from '../../utils/contractApplicability';

const ContractDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [terminateDialog, setTerminateDialog] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [terminateReason, setTerminateReason] = useState('');
  // 'applicable' | 'not_applicable' while the dialog is open
  const [applicabilityDialog, setApplicabilityDialog] = useState(null);
  const [expiryDialog, setExpiryDialog] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryReason, setExpiryReason] = useState('');
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [applicabilityReason, setApplicabilityReason] = useState('');
  const [savingApplicability, setSavingApplicability] = useState(false);
  const [recallDialog, setRecallDialog] = useState(false);
  const [recalling, setRecalling] = useState(false);
  const [recallReason, setRecallReason] = useState('');
  const [resubmitting, setResubmitting] = useState(false);
  const [renewalDialog, setRenewalDialog] = useState(false);
  const [renewalSaving, setRenewalSaving] = useState(false);
  const [renewalDraft, setRenewalDraft] = useState({ autoRenew: false, renewalTermMonths: '', renewalNoticePeriod: '30' });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(['basicInformation', 'contactDetails', 'contractInformation', 'activity']);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerUrl, setFileViewerUrl] = useState(null);
  const [fileViewerName, setFileViewerName] = useState('');

  const canOpen = canOpenContract(user);

  useEffect(() => {
    if (canOpen) fetchContract();
    else setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canOpen]);

  useEffect(() => {
    if (contract && searchParams.get('upload') === '1' && !contract.signedContract && contract.status !== 'active' && (user?.role === 'legal' || user?.role === 'super_admin')) {
      setUploadModalOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, searchParams]);

  const fetchContract = async () => {
    try {
      const response = await api.get(`/contracts/${id}`);
      setContract(response.data.data);
    } catch (error) {
      toast.error('Failed to load contract details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prevExpanded) => {
      if (isExpanded) {
        return prevExpanded.includes(panel) ? prevExpanded : [...prevExpanded, panel];
      } else {
        return prevExpanded.filter((p) => p !== panel);
      }
    });
  };

  const handleTerminateContract = async () => {
    if (!terminateReason.trim()) {
      toast.error('Please provide a termination reason');
      return;
    }
    setTerminating(true);
    try {
      await api.post(`/contracts/${id}/terminate`, { reason: terminateReason.trim() });
      toast.success('Contract terminated successfully');
      setTerminateDialog(false);
      setTerminateReason('');
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to terminate contract');
    } finally {
      setTerminating(false);
    }
  };

  const closeApplicabilityDialog = () => {
    setApplicabilityDialog(null);
    setApplicabilityReason('');
  };

  const handleContractApplicability = async () => {
    setSavingApplicability(true);
    try {
      const res = await api.post(`/approvals/${contract.supplier._id}/contract-applicability`, {
        applicability: applicabilityDialog,
        reason: applicabilityReason.trim() || undefined,
      });
      toast.success(res.data?.message || 'Contract applicability saved');
      closeApplicabilityDialog();
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save contract applicability');
    } finally {
      setSavingApplicability(false);
    }
  };

  // Legal can move the end date of a contract in force (or bring an expired one
  // back) without recalling it. The supplier is told the new date.
  const canChangeExpiry = (user?.role === 'legal' || user?.role === 'super_admin')
    && ['active', 'expired', 'termination_recommended'].includes(contract?.status);

  const openExpiryDialog = () => {
    setExpiryDate(contract?.endDate ? new Date(contract.endDate).toISOString().slice(0, 10) : '');
    setExpiryReason('');
    setExpiryDialog(true);
  };

  const handleChangeExpiry = async () => {
    setSavingExpiry(true);
    try {
      const res = await api.put(`/contracts/${id}/expiry`, { endDate: expiryDate, reason: expiryReason.trim() || undefined });
      toast.success(res.data?.message || 'Expiry date changed');
      setExpiryDialog(false);
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change the expiry date');
    } finally {
      setSavingExpiry(false);
    }
  };

  const handleRecallContract = async () => {
    setRecalling(true);
    try {
      await api.post(`/contracts/${id}/recall`, { reason: recallReason.trim() });
      toast.success('Contract recalled — the supplier has been told it is being corrected');
      setRecallDialog(false);
      setRecallReason('');
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to recall contract');
    } finally {
      setRecalling(false);
    }
  };

  const handleResubmitContract = async () => {
    setResubmitting(true);
    try {
      await api.post(`/contracts/${id}/resubmit`);
      toast.success('Contract resubmitted — the corrected copy has been sent to the supplier');
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resubmit contract');
    } finally {
      setResubmitting(false);
    }
  };

  const canManageRenewal = user?.role === 'legal' || user?.role === 'super_admin';
  const renewalLocked = ['terminated', 'expired'].includes(contract?.status);

  const openRenewalDialog = () => {
    const r = contract?.renewalOptions || {};
    setRenewalDraft({
      autoRenew: Boolean(r.autoRenew),
      renewalTermMonths: String(r.renewalTermMonths || contract?.validityMonths || ''),
      renewalNoticePeriod: String(r.renewalNoticePeriod ?? 30),
    });
    setRenewalDialog(true);
  };

  const handleSaveRenewal = async () => {
    setRenewalSaving(true);
    try {
      await api.put(`/contracts/${id}/renewal`, {
        autoRenew: renewalDraft.autoRenew,
        renewalTermMonths: renewalDraft.renewalTermMonths ? Number(renewalDraft.renewalTermMonths) : null,
        renewalNoticePeriod: renewalDraft.renewalNoticePeriod !== '' ? Number(renewalDraft.renewalNoticePeriod) : null,
      });
      toast.success(renewalDraft.autoRenew ? 'Auto-renew turned on' : 'Auto-renew turned off');
      setRenewalDialog(false);
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to save renewal settings');
    } finally {
      setRenewalSaving(false);
    }
  };

  const handleDownloadContract = async () => {
    if (!contract.signedContract?._id) return;
    try {
      const response = await api.get(`/documents/${contract.signedContract._id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', contract.signedContract.originalName || 'contract.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      let message = 'Failed to download contract';
      try {
        // responseType blob means error body is also a Blob — parse it to get the real message
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json.message) message = json.message;
        }
      } catch {}
      toast.error(message);
    }
  };

  const handleSaveContract = async (values) => {
    const formData = toFormData(values, contract?.supplier?._id);

    setUploading(true);
    try {
      await api.post(`/contracts/${id}/upload-signed`, formData);
      toast.success('Contract updated successfully');
      setUploadModalOpen(false);
      fetchContract();
    } catch (error) {
      console.error('Upload contract error:', error.response?.data || error);
      toast.error(error.response?.data?.message || 'Failed to upload contract');
    } finally {
      setUploading(false);
    }
  };

  const handleViewFile = async (document) => {
    let url;
    const fileName = document.originalName || document.fileName || 'Document';

    if (isFallbackDocument(document)) {
      const filePath = document.fileName || document.originalName;
      try {
        url = await fetchFileBlobUrl(buildUploadUrl(filePath, contract?.supplier?._id));
      } catch (error) {
        toast.error(error.message || 'Failed to open document');
        return;
      }
    } else {
      try {
        url = await fetchDocumentBlobUrl(document._id);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to open document');
        return;
      }
    }

    setFileViewerUrl(url);
    setFileViewerName(fileName);
    setFileViewerOpen(true);
  };

  const handleCloseFileViewer = () => {
    if (fileViewerUrl?.startsWith('blob:')) {
      window.URL.revokeObjectURL(fileViewerUrl);
    }
    setFileViewerOpen(false);
    setFileViewerUrl(null);
    setFileViewerName('');
  };

  const handleDownloadFile = async (document) => {
    if (isFallbackDocument(document)) {
      const filePath = document.fileName || document.originalName;
      try {
        await downloadFileFromPath(
          buildUploadUrl(filePath, contract?.supplier?._id),
          document.originalName || filePath
        );
      } catch (error) {
        toast.error(error.message || 'Failed to download document');
      }
    } else {
      try {
        await downloadDocument(document);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to download document');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const formatDocumentType = (type) => {
    const map = {
      'certificate_of_incorporation': 'Certificate of Incorporation',
      'cr12': 'CR12',
      'pin_certificate': 'KRA Pin Certificate',
      'company_profile': 'Company Profile',
      'bank_reference': 'Bank Reference Letter',
      'etims_registration': 'Proof of registration on E-tims',
      'practicing_certificate': 'Practicing Certificate',
      'member_resume': 'Resume',
      'directors_id': 'Directors ID',
      'audited_financials': 'Audited Financial Statements',
    };
    return map[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getEntityDocuments = () => {
    if (!supplier) return [];
    const entityDocs = [];
    if (supplier.documents && Array.isArray(supplier.documents)) {
      const apiDocs = supplier.documents.filter(doc =>
        ['certificate_of_incorporation', 'cr12', 'pin_certificate', 'company_profile', 'bank_reference', 'etims_registration', 'directors_id', 'audited_financials'].includes(doc.documentType)
      );
      if (apiDocs.length > 0) return apiDocs;
    }

    const docMap = {
      certificateOfIncorporation: { type: 'certificate_of_incorporation', name: 'Certificate of Incorporation' },
      cr12: { type: 'cr12', name: 'CR12' },
      kraPinCertificate: { type: 'pin_certificate', name: 'KRA Pin Certificate' },
      companyProfile: { type: 'company_profile', name: 'Company Profile' },
      bankReferenceLetter: { type: 'bank_reference', name: 'Bank Reference Letter' },
      etimsProof: { type: 'etims_registration', name: 'Proof of registration on E-tims' },
      financialStatements: { type: 'audited_financials', name: 'Audited Financial Statements' },
    };

    Object.keys(docMap).forEach(key => {
      if (supplier[key]) {
        entityDocs.push({
          _id: `doc-${key}`,
          documentType: docMap[key].type,
          originalName: docMap[key].name,
          fileSize: 2400000,
          fileName: supplier[key],
        });
      }
    });
    return entityDocs;
  };

  const getServiceDocuments = () => {
    if (!supplier) return [];
    const serviceDocs = [];
    if (supplier.documents && Array.isArray(supplier.documents)) {
      const apiDocs = supplier.documents.filter(doc =>
        ['practicing_certificate', 'member_resume'].includes(doc.documentType)
      );
      if (apiDocs.length > 0) return apiDocs;
    }

    if (supplier.practicingCertificates && Array.isArray(supplier.practicingCertificates)) {
      supplier.practicingCertificates.forEach((file, index) => {
        serviceDocs.push({
          _id: `practicing-${index}`,
          documentType: 'practicing_certificate',
          originalName: 'Practicing Certificate',
          fileSize: 2400000,
          fileName: file,
        });
      });
    }

    if (supplier.keyMembersResumes && Array.isArray(supplier.keyMembersResumes)) {
      supplier.keyMembersResumes.forEach((file, index) => {
        serviceDocs.push({
          _id: `resume-${index}`,
          documentType: 'member_resume',
          originalName: `Resume ${index + 1}`,
          fileSize: 2400000,
          fileName: file,
        });
      });
    }
    return serviceDocs;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const day = d.getDate();
    const year = d.getFullYear();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const month = months[d.getMonth()];

    // Add ordinal suffix
    const suffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };

    return `${day}${suffix(day)} ${month}, ${year}`;
  };

  const formatApplicationNumber = (application) => {
    if (!application?._id) return '-';
    const year = application.createdAt ? new Date(application.createdAt).getFullYear() : new Date().getFullYear();
    const shortId = application._id.toString().slice(-3).padStart(3, '0').toUpperCase();
    return `APP-${year}-${shortId}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!canOpen) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Department heads can see their department's contracts in the contract list, but cannot open them.
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/dashboard')} sx={{ textTransform: 'none' }}>
          Back to contracts
        </Button>
      </Container>
    );
  }

  if (!contract) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Contract not found</Alert>
      </Container>
    );
  }

  const supplier = contract.supplier || {};
  const applicationNumber = supplier.applicationNumber || contract.applicationNumber || formatApplicationNumber(supplier);

  const accordionStyle = {
    boxShadow: 'none',
    border: 'none',
    borderRadius: '0 !important',
    mb: 0,
    '&:before': { display: 'none' },
    '&.Mui-expanded': {
      margin: 0,
    },
  };

  const summaryStyle = (panel) => ({
    px: { xs: 2, sm: 3 },
    py: 2,
    minHeight: '56px',
    '&.Mui-expanded': {
      minHeight: '56px',
      borderBottom: 'none',
      pb: 2,
    },
    '& .MuiAccordionSummary-content': {
      my: 0,
    },
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: { xs: '16px', sm: '24px' },
      right: { xs: '16px', sm: '24px' },
      height: '1px',
      backgroundColor: '#e5e7eb',
      zIndex: 1,
    },
  });

  const labelStyle = {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: 500,
    mb: 0.5,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const valueStyle = {
    fontSize: '14px',
    color: '#111827',
    fontWeight: 400,
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', p: { xs: 0, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, px: { xs: 2, sm: 0 }, pt: { xs: 2, sm: 0 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => navigate('/contracts')} sx={{ color: '#111827', display: { sm: 'none' } }}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600, fontSize: '20px', color: '#111827' }}>
                {contract.contractNumber}
              </Typography>
            </Box>
            <StatusLabel kind="contract" status={contract.status || 'pending_upload'} label={contract.status?.replace(/_/g, ' ').toUpperCase() || 'PENDING UPLOAD'} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={{
                textTransform: 'none',
                borderColor: '#d1d5db',
                color: '#374151',
                fontSize: '14px',
                fontWeight: 500,
                px: 3,
                py: 1,
                borderRadius: '6px',
                bgcolor: '#fff',
                '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' }
              }}
              onClick={handleDownloadContract}
              disabled={!contract.signedContract}
            >
              Download Contract
            </Button>
            {(user?.role === 'legal' || user?.role === 'super_admin') && contract.status !== 'terminated' && (
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                sx={{
                  bgcolor: '#578A18',
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#467014', boxShadow: 'none' }
                }}
                onClick={() => setUploadModalOpen(true)}
              >
                Upload New Contract
              </Button>
            )}
          </Box>
        </Box>

        <Chip
          label={applicationNumber !== '-' ? `Application ${applicationNumber}` : 'Application'}
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            bgcolor: '#f3f4f6',
            color: '#374151',
            fontSize: '12px',
            fontWeight: 400,
            height: '24px',
            borderRadius: '12px',
            '& .MuiChip-label': { px: 1.5 }
          }}
        />
      </Box>

      {/* Legal / Super Admin: whether this prequalified supplier needs a contract */}
      {canSetApplicability(user, supplier) && (
        <Box sx={{ mb: 3, p: 2.5, border: '1px solid #e5e7eb', borderRadius: '8px', bgcolor: '#f9fafb', maxWidth: '1400px', mx: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ fontWeight: 600, color: '#111827' }}>
              Contract: {APPLICABILITY_LABEL[supplier.contractApplicability] || 'not yet decided'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {supplier.status === 'completed'
                ? 'Onboarding was completed without a contract. Marking it applicable returns the supplier to contract upload.'
                : 'Mark not applicable to complete onboarding without a contract, or applicable to upload one.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {supplier.contractApplicability !== 'applicable' && (
              <Button
                variant="outlined"
                onClick={() => setApplicabilityDialog('applicable')}
                sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151', bgcolor: '#fff', '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}
              >
                Contract Applicable
              </Button>
            )}
            {supplier.status === 'pending_contract_upload' && (
              <Button
                variant="outlined"
                onClick={() => setApplicabilityDialog('not_applicable')}
                sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151', bgcolor: '#fff', '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}
              >
                Contract Not Applicable
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Main Content Accordions */}
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: { xs: 0, sm: '8px' }, overflow: 'hidden', maxWidth: '1400px', mx: 'auto' }}>
        {/* Basic Information */}
        <Accordion expanded={expanded.includes('basicInformation')} onChange={handleAccordionChange('basicInformation')} sx={accordionStyle}>
          <AccordionSummary expandIcon={expanded.includes('basicInformation') ? <ExpandMore /> : <ChevronRight />} sx={summaryStyle('basicInformation')}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Basic Information</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Application Number</Typography>
                <Typography sx={valueStyle}>{applicationNumber}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Company Name</Typography>
                <Typography sx={valueStyle}>{supplier.supplierName || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Registered Country</Typography>
                <Typography sx={valueStyle}>{supplier.country || supplier.companyPhysicalAddress?.country || 'Kenya'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Company Registration Number</Typography>
                <Typography sx={valueStyle}>{supplier.companyRegistrationNumber || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Company Email Address</Typography>
                <Typography sx={valueStyle}>{supplier.companyEmail || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Company Website</Typography>
                <Typography sx={valueStyle}>{supplier.companyWebsite || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Legal Nature of Entity</Typography>
                <Typography sx={{ ...valueStyle, textTransform: 'capitalize' }}>{supplier.legalNature?.replace('_', ' ') || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={labelStyle}>Physical Address</Typography>
                <Typography sx={valueStyle}>
                  {supplier.companyPhysicalAddress ?
                    `${supplier.companyPhysicalAddress.street || ''}, ${supplier.companyPhysicalAddress.city || ''}, ${supplier.companyPhysicalAddress.country || ''}` :
                    '-'}
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Contact Details */}
        <Accordion expanded={expanded.includes('contactDetails')} onChange={handleAccordionChange('contactDetails')} sx={accordionStyle}>
          <AccordionSummary expandIcon={expanded.includes('contactDetails') ? <ExpandMore /> : <ChevronRight />} sx={summaryStyle('contactDetails')}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Contact Details</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Full Name</Typography>
                <Typography sx={valueStyle}>{supplier.authorizedPerson?.name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Relationship</Typography>
                <Typography sx={{ ...valueStyle, textTransform: 'capitalize' }}>{supplier.authorizedPerson?.relationship || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Email</Typography>
                <Typography sx={valueStyle}>{supplier.authorizedPerson?.email || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Phone</Typography>
                <Typography sx={valueStyle}>{supplier.authorizedPerson?.phone || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>ID/Passport Number</Typography>
                <Typography sx={valueStyle}>{supplier.authorizedPerson?.idPassportNumber || '-'}</Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Entity Details */}
        <Accordion expanded={expanded.includes('entityDetails')} onChange={handleAccordionChange('entityDetails')} sx={accordionStyle}>
          <AccordionSummary expandIcon={expanded.includes('entityDetails') ? <ExpandMore /> : <ChevronRight />} sx={summaryStyle('entityDetails')}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Entity Details</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Entity Type</Typography>
                <Typography sx={{ ...valueStyle, textTransform: 'capitalize', mb: 3 }}>{supplier.entityType?.replace('_', ' ') || '-'}</Typography>
              </Grid>
            </Grid>

            {/* Attached Documents */}
            {[...getEntityDocuments(), ...getServiceDocuments()].length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={labelStyle}>Attached Documents</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[...getEntityDocuments(), ...getServiceDocuments()].map((doc) => (
                    <Box key={doc._id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, border: '1px solid #e5e7eb', borderRadius: '8px', bgcolor: '#fff' }}>
                      <Box component="img" src="/images/File.svg" alt="File" sx={{ width: 24, height: 24 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '14px', color: '#111827', fontWeight: 400, mb: 0.5 }}>
                          {formatDocumentType(doc.documentType)}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                          PDF • {formatFileSize(doc.fileSize)}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleViewFile(doc)} sx={{ color: '#6b7280' }}>
                        <Box component="img" src="/images/eye.svg" alt="View" sx={{ width: 20, height: 20 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDownloadFile(doc)} sx={{ color: '#6b7280' }}>
                        <Box component="img" src="/images/Download.svg" alt="Download" sx={{ width: 20, height: 20 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 4 }}>
              <Typography sx={labelStyle}>Service Type</Typography>
              <Typography sx={{ ...valueStyle, textTransform: 'capitalize', mb: 3 }}>{supplier.serviceType?.replace('_', ' ') || '-'}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography sx={labelStyle}>Credit Period</Typography>
              <Typography sx={valueStyle}>{supplier.creditPeriod ? `${supplier.creditPeriod} Days` : '-'}</Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Source of Funds Declaration */}
        <Accordion expanded={expanded.includes('sourceOfFunds')} onChange={handleAccordionChange('sourceOfFunds')} sx={accordionStyle}>
          <AccordionSummary expandIcon={expanded.includes('sourceOfFunds') ? <ExpandMore /> : <ChevronRight />} sx={summaryStyle('sourceOfFunds')}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Source of Funds Declaration</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Source of Wealth</Typography>
                <Typography sx={{ ...valueStyle, textTransform: 'capitalize' }}>
                  {supplier.sourceOfFunds?.source ? supplier.sourceOfFunds.source.replace('_', ' ') : (supplier.sourceOfWealth || '-')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Declarant Full Name</Typography>
                <Typography sx={valueStyle}>{supplier.sourceOfFunds?.declarantName || supplier.declarantFullName || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Declarant Capacity</Typography>
                <Typography sx={valueStyle}>{supplier.sourceOfFunds?.declarantCapacity || supplier.declarantCapacity || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>ID/Passport Number</Typography>
                <Typography sx={valueStyle}>{supplier.sourceOfFunds?.declarantIdPassport || supplier.declarantIdPassport || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Declaration Date</Typography>
                <Typography sx={valueStyle}>{formatDate(supplier.sourceOfFunds?.declarationDate)}</Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Personal Information Processing Consent */}
        <Accordion expanded={expanded.includes('consent')} onChange={handleAccordionChange('consent')} sx={accordionStyle}>
          <AccordionSummary expandIcon={expanded.includes('consent') ? <ExpandMore /> : <ChevronRight />} sx={summaryStyle('consent')}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Personal Information Processing Consent</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Full Name of Declarant</Typography>
                <Typography sx={valueStyle}>{supplier.declarantFullName || supplier.dataProcessingConsent?.consentorName || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Date</Typography>
                <Typography sx={valueStyle}>{formatDate(supplier.declarationDate || supplier.dataProcessingConsent?.consentDate)}</Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Contract Information */}
        <Accordion expanded={expanded.includes('contractInformation')} onChange={handleAccordionChange('contractInformation')} sx={accordionStyle}>
          <AccordionSummary expandIcon={expanded.includes('contractInformation') ? <ExpandMore /> : <ChevronRight />} sx={summaryStyle('contractInformation')}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Contract Information</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Contract Type</Typography>
                <Typography sx={valueStyle}>{contract.contractType === 'services' ? 'Service Agreement' : 'Other Agreement'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Contract Status</Typography>
                <StatusLabel kind="contract" status={displayContractStatus(contract)} label={contractStatusLabel(displayContractStatus(contract))} size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Department</Typography>
                <Typography sx={valueStyle}>{contract.department || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Contract Validity</Typography>
                <Typography sx={valueStyle}>{contract.validityMonths ? `${contract.validityMonths} Months` : '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Notice Period</Typography>
                <Typography sx={valueStyle}>{contract.noticePeriodMonths ? `${contract.noticePeriodMonths} Months` : '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Start Date</Typography>
                <Typography sx={valueStyle}>{formatDate(contract.startDate)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={labelStyle}>Expiry Date</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={valueStyle}>{formatDate(contract.endDate)}</Typography>
                  {canChangeExpiry && (
                    <Button size="small" onClick={openExpiryDialog} sx={{ textTransform: 'none', minWidth: 0, color: '#578A18' }}>
                      Change
                    </Button>
                  )}
                </Box>
              </Grid>
              {contract.notes && (
                <Grid item xs={12}>
                  <Typography sx={labelStyle}>Notes</Typography>
                  <Typography sx={valueStyle}>{contract.notes}</Typography>
                </Grid>
              )}
            </Grid>

            {/* Auto-renewal: legal decides; everyone else can see what will happen */}
            <Box sx={{ mt: 4, p: 3, border: '1px solid #e5e7eb', borderRadius: '8px', bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5, color: '#111827' }}>
                    Auto-renew: {contract.renewalOptions?.autoRenew ? 'On' : 'Off'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contract.renewalOptions?.autoRenew
                      ? `Renews for ${contract.renewalOptions.renewalTermMonths || contract.validityMonths} months on ${formatDate(contract.endDate)}. `
                        + `Legal and the department head are warned ${contract.renewalOptions.renewalNoticePeriod ?? 30} days before.`
                      : 'This contract expires at its end date unless legal turns auto-renew on.'}
                  </Typography>
                </Box>
                {canManageRenewal && !renewalLocked && (
                  <Button
                    variant="outlined"
                    sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151', '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}
                    onClick={openRenewalDialog}
                  >
                    Change
                  </Button>
                )}
              </Box>
              {contract.renewals?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={labelStyle}>Renewal history</Typography>
                  {contract.renewals.map((r, i) => (
                    <Typography key={r._id || i} variant="body2" color="text.secondary">
                      {formatDate(r.renewedAt)}: {formatDate(r.previousEndDate)} → {formatDate(r.newEndDate)} ({r.termMonths} months)
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>

            {/* Recalled: shown to every staff role, with the way out for legal */}
            {contract.status === 'recalled' && (
              <Box sx={{ mt: 4, p: 3, border: '1px solid #fbbf24', borderRadius: '8px', bgcolor: '#fffbeb' }}>
                <Typography sx={{ fontWeight: 600, mb: 1, color: '#92400e' }}>Recalled for Correction</Typography>
                {contract.recallReason && (
                  <Typography variant="body2" color="#78350f" sx={{ mb: 0.5 }}>
                    Reason: {contract.recallReason}
                  </Typography>
                )}
                <Typography variant="body2" color="#78350f">
                  Recalled {formatDate(contract.recalledAt)}
                  {contract.recalledBy?.firstName ? ` by ${contract.recalledBy.firstName} ${contract.recalledBy.lastName || ''}` : ''}
                </Typography>
                {(user?.role === 'legal' || user?.role === 'super_admin') && (
                  <Box sx={{ mt: 2, bgcolor: '#fef3c7', p: 2, borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="#92400e" sx={{ fontWeight: 500 }}>
                      Use Upload New Contract to replace the document or its dates, then resubmit. The supplier is sent the corrected copy.
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ textTransform: 'none', bgcolor: '#578A18', boxShadow: 'none', '&:hover': { bgcolor: '#467014', boxShadow: 'none' } }}
                      onClick={handleResubmitContract}
                      disabled={resubmitting || !contract.signedContract}
                    >
                      {resubmitting ? 'Resubmitting...' : 'Resubmit Contract'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Legal / Super Admin: recall a live contract to correct it */}
            {(user?.role === 'legal' || user?.role === 'super_admin') && contract.status === 'active' && (
              <Box sx={{ mt: 4, p: 3, border: '1px solid #fbbf24', borderRadius: '8px', bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>Recall Contract</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Withdraw this contract to correct it. The supplier is told it is being corrected, and is sent the new copy when you resubmit.
                </Typography>
                <Box sx={{ bgcolor: '#fef3c7', p: 2, borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="#92400e" sx={{ fontWeight: 500 }}>The contract is not active while recalled.</Typography>
                  <Button
                    variant="outlined"
                    sx={{ textTransform: 'none', borderColor: '#d97706', color: '#92400e', '&:hover': { borderColor: '#b45309', bgcolor: '#fffbeb' } }}
                    onClick={() => setRecallDialog(true)}
                  >
                    Recall Contract
                  </Button>
                </Box>
              </Box>
            )}

            {/* Legal / Super Admin: pending recommendation details */}
            {(user?.role === 'legal' || user?.role === 'super_admin') && contract.status === 'termination_recommended' && (
              <Box sx={{ mt: 4, p: 3, border: '1px solid #fbbf24', borderRadius: '8px', bgcolor: '#fffbeb' }}>
                <Typography sx={{ fontWeight: 600, mb: 1, color: '#92400e' }}>Termination Recommendation Received</Typography>
                {contract.terminationRecommendationReason && (
                  <Typography variant="body2" color="#78350f" sx={{ mb: 0.5 }}>
                    Reason: {contract.terminationRecommendationReason}
                  </Typography>
                )}
                {contract.terminationRecommendedBy && (
                  <Typography variant="body2" color="#78350f" sx={{ mb: 0.5 }}>
                    Recommended by: {contract.terminationRecommendedBy.firstName} {contract.terminationRecommendedBy.lastName}
                  </Typography>
                )}
                {contract.terminationRecommendedAt && (
                  <Typography variant="body2" color="#78350f">
                    Date: {formatDate(contract.terminationRecommendedAt)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Legal / Super Admin: actualize termination */}
            {(user?.role === 'legal' || user?.role === 'super_admin') && (contract.status === 'active' || contract.status === 'termination_recommended') && (
              <Box sx={{ mt: 4, p: 3, border: '1px solid #fca5a5', borderRadius: '8px', bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>Terminate Contract</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  This will send a notification of termination to the supplier. Please note that this action is irreversible.
                </Typography>
                <Box sx={{ bgcolor: '#fee2e2', p: 2, borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="#b91c1c" sx={{ fontWeight: 500 }}>This action cannot be undone!</Typography>
                  <Button
                    variant="contained"
                    color="error"
                    sx={{ textTransform: 'none', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                    onClick={() => setTerminateDialog(true)}
                  >
                    Terminate Contract
                  </Button>
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Activity: internal comments and what has happened to the contract */}
        {canSeeActivity(user) && (
          <Accordion expanded={expanded.includes('activity')} onChange={handleAccordionChange('activity')} sx={accordionStyle}>
            <AccordionSummary expandIcon={expanded.includes('activity') ? <ExpandMore /> : <ChevronRight />} sx={summaryStyle('activity')}>
              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Activity</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
              <ContractActivity contractId={contract._id} />
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      {/* Modals & Dialogs */}
      <UploadContractModal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} onSave={handleSaveContract} uploading={uploading} contract={contract} />

      <Dialog open={fileViewerOpen} onClose={handleCloseFileViewer} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '8px', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600, color: '#111827', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {fileViewerName}
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#f5f5f5' }}>
          {fileViewerUrl && (
            <Box sx={{ width: '100%', height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {fileViewerName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img src={fileViewerUrl} alt={fileViewerName} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              ) : (
                <iframe src={fileViewerUrl} style={{ width: '100%', height: '100%', border: 'none' }} title={fileViewerName} />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileViewer}>Close</Button>
          <Button variant="contained" onClick={() => window.open(fileViewerUrl, '_blank')}>Open in New Tab</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={terminateDialog} onClose={() => { setTerminateDialog(false); setTerminateReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Termination</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Are you sure you want to terminate this contract? This action is irreversible.</Typography>
          <TextField
            label="Termination Reason"
            multiline
            rows={3}
            fullWidth
            required
            value={terminateReason}
            onChange={(e) => setTerminateReason(e.target.value)}
            placeholder="Provide a reason for termination..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTerminateDialog(false); setTerminateReason(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleTerminateContract} disabled={terminating || !terminateReason.trim()}>
            {terminating ? 'Terminating...' : 'Terminate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renewalDialog} onClose={() => !renewalSaving && setRenewalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto-renewal</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={(
              <Switch
                checked={renewalDraft.autoRenew}
                onChange={(e) => setRenewalDraft((d) => ({ ...d, autoRenew: e.target.checked }))}
              />
            )}
            label="Renew automatically at the end date"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Each renewal lasts (months)"
            type="number"
            fullWidth
            inputProps={{ min: 1, max: 120 }}
            value={renewalDraft.renewalTermMonths}
            onChange={(e) => setRenewalDraft((d) => ({ ...d, renewalTermMonths: e.target.value }))}
            disabled={!renewalDraft.autoRenew}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Warn legal and the department head (days before)"
            type="number"
            fullWidth
            inputProps={{ min: 0, max: 365 }}
            value={renewalDraft.renewalNoticePeriod}
            onChange={(e) => setRenewalDraft((d) => ({ ...d, renewalNoticePeriod: e.target.value }))}
            disabled={!renewalDraft.autoRenew}
            helperText="The supplier is told once the contract has renewed."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewalDialog(false)} disabled={renewalSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveRenewal}
            disabled={renewalSaving || (renewalDraft.autoRenew && !Number(renewalDraft.renewalTermMonths))}
            sx={{ bgcolor: '#578A18', '&:hover': { bgcolor: '#467014' } }}
          >
            {renewalSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={recallDialog} onClose={() => { if (!recalling) { setRecallDialog(false); setRecallReason(''); } }} maxWidth="sm" fullWidth>
        <DialogTitle>Recall Contract</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>The supplier will be told this contract has been withdrawn for correction, with the reason you give.</Typography>
          <TextField
            label="Reason for recall"
            multiline
            rows={3}
            fullWidth
            required
            value={recallReason}
            onChange={(e) => setRecallReason(e.target.value)}
            placeholder="What needs correcting..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRecallDialog(false); setRecallReason(''); }} disabled={recalling}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRecallContract} disabled={recalling || !recallReason.trim()}>
            {recalling ? 'Recalling...' : 'Recall'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={expiryDialog} onClose={() => setExpiryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Expiry Date</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            The contract stays {contract.status === 'expired' ? 'with the supplier and becomes active again' : 'active'}; it is not recalled.
            The supplier is notified of the new date.
          </Typography>
          <TextField
            label="New expiry date"
            type="date"
            fullWidth
            required
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().slice(0, 10) }}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Reason (optional)"
            multiline
            rows={3}
            fullWidth
            value={expiryReason}
            onChange={(e) => setExpiryReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpiryDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleChangeExpiry}
            disabled={savingExpiry || !expiryDate}
            sx={{ bgcolor: '#578A18', boxShadow: 'none', '&:hover': { bgcolor: '#467014', boxShadow: 'none' } }}
          >
            {savingExpiry ? 'Saving...' : 'Change Expiry Date'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(applicabilityDialog)} onClose={closeApplicabilityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{applicabilityDialog === 'not_applicable' ? 'Contract Not Applicable' : 'Contract Applicable'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {applicabilityDialog === 'not_applicable'
              ? 'The supplier\'s onboarding is completed without a contract, and the supplier is told no contract is required.'
              : 'The supplier stays with legal for the contract upload.'}
          </Typography>
          <TextField
            label={applicabilityDialog === 'not_applicable' ? 'Reason' : 'Reason (optional)'}
            multiline
            rows={3}
            fullWidth
            required={applicabilityDialog === 'not_applicable'}
            value={applicabilityReason}
            onChange={(e) => setApplicabilityReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApplicabilityDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleContractApplicability}
            disabled={savingApplicability || (applicabilityDialog === 'not_applicable' && !applicabilityReason.trim())}
            sx={{ bgcolor: '#578A18', boxShadow: 'none', '&:hover': { bgcolor: '#467014', boxShadow: 'none' } }}
          >
            {savingApplicability ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ContractDetails;
